import React from 'react';
import { useChatStore } from '../../store/chatStore';

import { useTranslation } from 'react-i18next';

export const ToolNavigation: React.FC = React.memo(() => {
  const { t } = useTranslation('pleri/chat');
  const selectedToolId = useChatStore((state) => state.selectedToolId);
  const setSelectedToolId = useChatStore((state) => state.setSelectedToolId);
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const conversation = useChatStore((state) =>
    activeConversationId ? state.conversations[activeConversationId] : null,
  );

  // Get all tool executions
  const toolExecutions = React.useMemo(() => {
    if (!conversation) {
      return [];
    }
    const executions: { id: string; name: string }[] = [];

    for (const message of conversation.messages) {
      for (const block of message.content) {
        if (block.type === 'toolUse') {
          executions.push({ id: block.id, name: block.name });
        }
      }
    }
    return executions;
  }, [conversation]);

  // Find current tool index
  const currentToolIndex = React.useMemo(() => {
    if (!selectedToolId) {
      return -1;
    }
    return toolExecutions.findIndex((tool) => tool.id === selectedToolId);
  }, [selectedToolId, toolExecutions]);

  const scrollToToolBlock = (
    toolId: string,
    isFirst: boolean,
    isLast: boolean,
  ) => {
    // Find the tool block element
    const toolBlock = document.querySelector(`[data-tool-id="${toolId}"]`);
    if (toolBlock) {
      if (isFirst || isLast) {
        // For first or last tool, scroll to top or bottom
        // It's due to a glitch in the scrollIntoView that doesn't work well with the first or last tool
        toolBlock.scrollIntoView({ block: 'center' });
      } else {
        // For middle tools, smooth scroll to center
        toolBlock.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  };

  const handlePrevTool = () => {
    if (currentToolIndex > 0) {
      const prevToolId = toolExecutions[currentToolIndex - 1].id;
      const isFirst = currentToolIndex - 1 === 0;
      setSelectedToolId(prevToolId);
      scrollToToolBlock(prevToolId, isFirst, false);
    }
  };

  const handleNextTool = () => {
    if (currentToolIndex < toolExecutions.length - 1) {
      const nextToolId = toolExecutions[currentToolIndex + 1].id;
      const isLast = currentToolIndex + 1 === toolExecutions.length - 1;
      setSelectedToolId(nextToolId);
      scrollToToolBlock(nextToolId, false, isLast);
    }
  };

  if (currentToolIndex < 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
        {t('currentOfTotal', {
          current: currentToolIndex + 1,
          total: toolExecutions.length,
        })}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={handlePrevTool}
          disabled={currentToolIndex <= 0}
          className={`flex h-7 w-7 items-center justify-center rounded-md border transition-all ${
            currentToolIndex <= 0
              ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300 dark:border-gray-700 dark:bg-gray-800'
              : 'border-orange-200 bg-white text-[#FF732E] hover:border-[#FF732E] hover:bg-orange-50 active:bg-orange-100 dark:border-gray-600 dark:bg-gray-900'
          }`}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
        </button>
        <button
          onClick={handleNextTool}
          disabled={currentToolIndex >= toolExecutions.length - 1}
          className={`flex h-7 w-7 items-center justify-center rounded-md border transition-all ${
            currentToolIndex >= toolExecutions.length - 1
              ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300 dark:border-gray-700 dark:bg-gray-800'
              : 'border-orange-200 bg-white text-[#FF732E] hover:border-[#FF732E] hover:bg-orange-50 active:bg-orange-100 dark:border-gray-600 dark:bg-gray-900'
          }`}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2.5"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.25 4.5l7.5 7.5-7.5 7.5"
            />
          </svg>
        </button>
      </div>
    </div>
  );
});

ToolNavigation.displayName = 'ToolNavigation';
